import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { Logo, LangToggle } from "../../components/marketing/Navbar";
import GoogleButton from "../../components/marketing/GoogleButton";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { t } = useI18n();
  const { login, guestLogin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("login_title"));
      window.location.assign("/app");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const quick = async () => {
    setLoading(true);
    try {
      await guestLogin();
      window.location.assign("/app");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex gc-gradient-navy text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 gc-hero-grid opacity-30" />
        <div className="relative"><Logo light /></div>
        <div className="relative">
          <h2 className="font-display text-3xl font-bold leading-tight">{t("hero_title")}</h2>
          <p className="mt-4 text-white/70 max-w-md">{t("positioning")}</p>
        </div>
        <div className="relative text-xs text-white/40">{t("footer_copy")}</div>
      </div>
      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex justify-between items-center">
          <div className="lg:hidden"><Logo /></div>
          <div className="ms-auto"><LangToggle /></div>
        </div>
        <form onSubmit={submit} className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          <h1 className="font-display text-3xl font-bold text-[hsl(217_63%_15%)]">{t("login_title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("login_sub")}</p>
          <button type="button" data-testid="quick-access-btn" disabled={loading} onClick={quick}
            className="mt-6 w-full h-11 rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)] text-white font-medium transition-colors">
            {t("quick_access")}
          </button>
          <p className="mt-1.5 text-xs text-muted-foreground text-center">{t("quick_access_hint")}</p>
          <div className="mt-5"><GoogleButton /></div>
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><span className="flex-1 h-px bg-border" />{t("or_sep")}<span className="flex-1 h-px bg-border" /></div>
          <div className="space-y-4">
            <div><Label>{t("identifier_label")}</Label><Input data-testid="login-email" required className="mt-1.5 h-11" placeholder={t("identifier_ph")} value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div><Label>{t("password")}</Label><Input data-testid="login-password" type="password" required className="mt-1.5 h-11" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          </div>
          <Button data-testid="login-submit" disabled={loading} className="mt-7 h-11 rounded-full bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">{loading ? t("loading") : t("sign_in")}</Button>
          <p className="mt-6 text-sm text-muted-foreground text-center">{t("no_account")} <Link to="/register" className="text-[hsl(158_64%_28%)] font-medium">{t("sign_up")}</Link></p>
          <div className="mt-6 border-t border-border pt-4">
            <div className="text-xs text-muted-foreground text-center mb-2">{t("quick_demo")}</div>
            <div className="grid grid-cols-2 gap-2">
              {[["client_admin", "Client"], ["vat_specialist", "Specialist"], ["supervisor", "Supervisor"], ["quality_reviewer", "Quality"], ["management", "Management"]].map(([role, label]) => (
                <button key={role} type="button" data-testid={`demo-${role}`} disabled={loading}
                  onClick={async () => { setLoading(true); try { await api.post(`/auth/demo/${role}`); window.location.href = "/app"; } catch { setLoading(false); } }}
                  className="text-xs rounded-lg border border-border px-3 py-2 hover:border-[hsl(158_64%_28%)] hover:bg-secondary transition-colors">
                  {label}
                </button>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
